import { Send } from "lucide-react";
import { lazy, Suspense, useEffect, useRef, useState, type FormEvent } from "react";
import type { Conversation, RevisionProposal, TermBlock, TermDetail, Turn } from "@termclear/contracts";
import { api } from "../api";
import {
  patchChatDraft,
  readChatDraft,
  removeChatDraft,
  type DraftOperationIdentity,
  type StoredChatDraft,
} from "../chat-draft";
import { Sheet } from "./Sheet";

const ModelMessageContent = lazy(() => import("./ModelMessageContent"));

const AUTO_EXPLANATION_PROMPT = "请把我点中的这段真正讲透，并说明它在当前任务里改变了什么。整篇回答都要使用清楚、有条理、不了解该领域的人也能顺着读懂的大白话，不是只给一句通俗概括；专业词第一次出现时请就地解释，机制请说明谁做什么、为什么、前后怎样变化和没做好会怎样，并按需要补充具体例子与容易混淆的边界。";

export function ChatSheet(props: {
  term: TermDetail;
  block: TermBlock;
  draftId: string;
  modelLabel: string;
  onClose: () => void;
  onApplied: () => void;
}) {
  const [restoredDraft] = useState(() => readChatDraft(
    window.sessionStorage,
    props.draftId,
    () => crypto.randomUUID(),
  ));
  const draft = useRef(restoredDraft);
  const mounted = useRef(false);
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [input, setInput] = useState(restoredDraft.input);
  const [proposal, setProposal] = useState<RevisionProposal | null>(restoredDraft.proposal);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function persistDraft(
    patch: Partial<StoredChatDraft>,
    expected?: DraftOperationIdentity,
  ): "written" | "stale" | "failed" {
    const result = patchChatDraft(window.sessionStorage, props.draftId, draft.current, patch, expected);
    draft.current = result.draft;
    return result.kind;
  }

  useEffect(() => {
    let active = true;
    mounted.current = true;
    setBusy(true);
    if (persistDraft({}) === "failed") {
      setBusy(false);
      setError(storageRequiredMessage());
      return () => { active = false; mounted.current = false; };
    }
    if (restoredDraft.conversationId) {
      api.getConversation(restoredDraft.conversationId).then(async (value) => {
        if (!active) return;
        setConversation(value);
        if (!draft.current.autoTurnPending) return;
        try {
          const operationKey = draft.current.autoTurnOperationKey;
          const turn = await api.createTurn(value.id, AUTO_EXPLANATION_PROMPT, operationKey);
          if (!active) return;
          persistDraft({ autoTurnPending: false }, { field: "autoTurnOperationKey", key: operationKey });
          assertSucceeded(turn);
          if (active) setConversation(await api.getConversation(value.id));
        } catch (cause) {
          if (active) {
            setConversation(await api.getConversation(value.id).catch(() => value));
            setError(cause instanceof Error ? cause.message : "模型没有完成自动解释");
          }
        }
      }).catch((cause: unknown) => {
        if (active) setError(cause instanceof Error ? cause.message : "没有恢复这次对话");
      }).finally(() => {
        if (active) setBusy(false);
      });
      return () => { active = false; mounted.current = false; };
    }
    const conversationOperationKey = draft.current.conversationOperationKey;
    api.createConversation(props.term.id, {
      entryBlockId: props.block.id,
      baselineRevisionId: props.term.currentRevision.id,
    }, conversationOperationKey).then(async (value) => {
      if (!active) return;
      if (persistDraft(
        { conversationId: value.id },
        { field: "conversationOperationKey", key: conversationOperationKey },
      ) !== "written") return;
      setConversation(value);
      try {
        const autoTurnOperationKey = draft.current.autoTurnOperationKey;
        const turn = await api.createTurn(value.id, AUTO_EXPLANATION_PROMPT, autoTurnOperationKey);
        if (!active) return;
        persistDraft(
          { autoTurnPending: false },
          { field: "autoTurnOperationKey", key: autoTurnOperationKey },
        );
        assertSucceeded(turn);
        if (active) setConversation(await api.getConversation(value.id));
      } catch (cause) {
        if (active) {
          setConversation(await api.getConversation(value.id).catch(() => value));
          setError(cause instanceof Error ? cause.message : "模型没有完成自动解释");
        }
      }
    }).catch((cause: unknown) => {
      if (active) setError(cause instanceof Error ? cause.message : "没有创建对话");
    }).finally(() => {
      if (active) setBusy(false);
    });
    return () => { active = false; mounted.current = false; };
  }, [props.block.id, props.draftId, props.term.currentRevision.id, props.term.id, restoredDraft.conversationId]);

  async function refreshConversation(id: string) {
    const value = await api.getConversation(id);
    if (mounted.current) setConversation(value);
  }

  async function send(event: FormEvent) {
    event.preventDefault();
    const content = input.trim();
    if (!conversation || !content || busy || proposal) return;
    const operation = draft.current.sendOperation?.content === content
      ? draft.current.sendOperation
      : { content, key: crypto.randomUUID() };
    if (persistDraft({ sendOperation: operation }) === "failed") {
      setError(storageRequiredMessage());
      return;
    }
    setBusy(true);
    setError("");
    try {
      const turn = await api.createTurn(conversation.id, content, operation.key);
      assertSucceeded(turn);
      const value = await api.getConversation(conversation.id);
      if (!mounted.current) return;
      const persisted = persistDraft(
        { sendOperation: null, input: "" },
        { field: "sendOperation", key: operation.key },
      );
      if (persisted === "stale") return;
      setConversation(value);
      setInput("");
      if (persisted === "failed") setError(storageRequiredMessage());
    } catch (cause) {
      if (!mounted.current) return;
      await refreshConversation(conversation.id).catch(() => undefined);
      if (mounted.current) setError(cause instanceof Error ? cause.message : "模型没有完成回答");
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  async function generateProposal() {
    if (!conversation || busy) return;
    setBusy(true);
    setError("");
    const operationKey = draft.current.proposalOperationKey ?? crypto.randomUUID();
    if (persistDraft({ proposalOperationKey: operationKey }) === "failed") {
      setBusy(false);
      setError(storageRequiredMessage());
      return;
    }
    try {
      const value = await api.createProposal(conversation.id, operationKey);
      if (!mounted.current) return;
      const persisted = persistDraft(
        { proposal: value, proposalOperationKey: null },
        { field: "proposalOperationKey", key: operationKey },
      );
      if (persisted === "stale") return;
      setProposal(value);
      if (persisted === "failed") setError(storageRequiredMessage());
    } catch (cause) {
      if (mounted.current) setError(cause instanceof Error ? cause.message : "没有生成更新预览");
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  async function apply() {
    if (!proposal || busy) return;
    setBusy(true);
    setError("");
    const operation = draft.current.applyOperation?.proposalId === proposal.id
      ? draft.current.applyOperation
      : { proposalId: proposal.id, key: crypto.randomUUID() };
    if (persistDraft({ applyOperation: operation }) === "failed") {
      setBusy(false);
      setError(storageRequiredMessage());
      return;
    }
    try {
      await api.applyProposal(proposal, operation.key);
      if (!mounted.current) return;
      const removed = removeChatDraft(
        window.sessionStorage,
        props.draftId,
        draft.current,
        { field: "applyOperation", key: operation.key },
      );
      draft.current = removed.draft;
      if (removed.kind === "stale") return;
      props.onApplied();
    } catch (cause) {
      if (mounted.current) setError(cause instanceof Error ? cause.message : "没有应用新版本");
    } finally {
      if (mounted.current) setBusy(false);
    }
  }

  return (
    <Sheet
      title="这段没懂"
      description={`${props.modelLabel} · 从当前块开始，可补清同一术语的多个块`}
      onClose={props.onClose}
      className="chat-sheet"
    >
      <div className="chat-layout">
        <div className="chat-scroll">
          <blockquote className="selected-quote">{props.block.body}</blockquote>
          <div className="chat-messages" aria-live="polite" aria-busy={busy}>
            {conversation?.messages.length ? conversation.messages.map((message) => (
              <article className={`message ${message.role}`} key={message.id}>
                <span>{message.role === "user" ? "我" : props.modelLabel}</span>
                {message.role === "assistant" ? (
                  <Suspense fallback={<p>{message.content}</p>}>
                    <ModelMessageContent content={message.content} />
                  </Suspense>
                ) : <p>{message.content}</p>}
              </article>
            )) : (
              <p className="chat-empty">说出具体卡住的地方。对话只围绕当前术语保存。</p>
            )}
          </div>
          {error ? <p className="model-error" role="alert">{error}</p> : null}
          {proposal ? <ProposalPreview proposal={proposal} onApply={() => void apply()} busy={busy} /> : null}
        </div>
        <footer className="chat-footer">
          <form className="chat-composer" onSubmit={send}>
            <label className="visually-hidden" htmlFor="chat-input">继续追问</label>
            <textarea
              id="chat-input"
              rows={2}
              placeholder="继续追问…"
              value={input}
              onChange={(event) => {
                setInput(event.target.value);
                persistDraft({ input: event.target.value });
              }}
              disabled={!conversation || busy || Boolean(proposal)}
            />
            <button className="send-button" type="submit" aria-label="发送" disabled={!input.trim() || busy || Boolean(proposal)}>
              <Send aria-hidden="true" />
            </button>
          </form>
          <div className="chat-actions">
            <p>{proposal ? "预览已冻结本次对话；应用或关闭后可重新发起对话。" : "AI 可建议更新同一术语的多个块；你确认前不会修改正文。"}</p>
            <button className="primary-button" type="button" onClick={() => void generateProposal()} disabled={!conversation || busy || Boolean(proposal)}>
              {busy ? "处理中…" : proposal ? "预览已生成" : "生成更新预览"}
            </button>
          </div>
        </footer>
      </div>
    </Sheet>
  );
}

function assertSucceeded(turn: Turn): void {
  if (turn.status === "succeeded") return;
  if (turn.status === "unknown") {
    throw new Error("上一次模型调用的结果无法确认；为避免重复计费，系统没有自动再次调用");
  }
  throw new Error(turn.errorCode ? `模型没有完成回答（${turn.errorCode}）` : "模型没有完成回答");
}

function storageRequiredMessage(): string {
  return "浏览器无法保存安全重试信息；请允许本站使用会话存储后再继续";
}

function ProposalPreview(props: { proposal: RevisionProposal; onApply: () => void; busy: boolean }) {
  return (
    <section className="update-preview" aria-labelledby="preview-title">
      <div className="preview-heading">
        <div><p>更新预览</p><h3 id="preview-title">建议更新 {props.proposal.blockChanges.length} 个内容块</h3></div>
        <span>AI 候选</span>
      </div>
      <p className="preview-scope-note">{props.proposal.summary}</p>
      <div className="preview-block-list">
        {props.proposal.blockChanges.map((change) => (
          <article className="preview-block" key={change.blockId}>
            <h4>{change.title}</h4>
            <div className="preview-copy"><h5>原解释</h5><p>{change.oldBody}</p></div>
            <div className="preview-copy new-copy"><h5>建议新解释</h5><p>{change.newBody}</p></div>
          </article>
        ))}
      </div>
      <section className="question-summary">
        <h4>从对话中保留的疑问与回答</h4>
        {props.proposal.questionPoints.length ? props.proposal.questionPoints.map((point) => (
          <article className="question-point" key={point.id}>
            <blockquote>“{point.question}”</blockquote>
            <p>{point.answer}</p>
          </article>
        )) : <p className="muted">本次没有明确疑问点，不会凭空补写。</p>}
      </section>
      <p className="preview-note">只有应用后才会创建不可变的新 revision。</p>
      <button className="primary-button full-width" type="button" onClick={props.onApply} disabled={props.busy}>
        应用为新版本
      </button>
    </section>
  );
}
